import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  addCardDetails,
  addCoupon,
  deleteCardDetail,
  getBillingHistories,
  getBillingHistory,
  getBusinessPlan,
  getBusinessInvoiceTransactions,
  getCardDetails,
  markBillingStatusPaid,
  markBillingStatusVoid,
  fetchBusinessInvoice,
  payDues,
  removeCoupon,
  updateCardDetail,
  cancelBusinessSubscription
} from './api';
import { BusinessPlanResponse, CardDetailsResponse, InvoiceTransactionResponse } from './types';
import type { CancelSubscriptionPayload } from '@/components/Common/CancelSubscription/types';
import { queryClient } from '@/providers/AllProviders';

export function useGetBusinessBillingHistories(params?: Record<string, any>, options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['billing_histories'],
    queryFn: () => getBillingHistories(params),
    ...options
  });
}

export function useGetBusinessBillingHistory(options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['billing_history'],
    queryFn: () => getBillingHistory(),
    ...options
  });
}

export function useGetBusinessCancellationPreview(options?: UseQueryOptions<any, Error>) {
  return useQuery<any>({
    queryKey: ['billing_history_cancellation_preview'],
    queryFn: () => getBillingHistory({ cancellation_preview: true }),
    ...options
  });
}

export function useGetCardDetails(options?: any) {
  return useQuery<CardDetailsResponse>({
    queryKey: ['billing-card-details'],
    queryFn: () => getCardDetails(),
    ...options
  });
}

export function useUpdateCardDetails() {
  return useMutation({
    mutationFn: (id: string) => updateCardDetail(id),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['billing-card-details']
      });
    }
  });
}

export function useDeleteCard() {
  return useMutation({
    mutationFn: (id: string) => deleteCardDetail(id),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['billing-card-details']
      });
    }
  });
}

export function useAddCardDetails() {
  return useMutation({
    mutationFn: () => addCardDetails({ redirect_uri: window.location.href }),
    onSuccess: (data: { checkout_url: string }) => {
      if (data.checkout_url) window.open(data.checkout_url, '_blank');
    }
  });
}

export function useGetBusinessPlan(id?: number, options?: UseQueryOptions<BusinessPlanResponse, Error>) {
  return useQuery<BusinessPlanResponse>({
    queryKey: ['business_plan'],
    queryFn: () => getBusinessPlan(id!),
    enabled: Boolean(id),
    ...options
  });
}

export function useAddCoupon() {
  return useMutation({
    mutationFn: (data: { business_id: number; code: string }) => addCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['billing_history'],
        refetchType: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['billing_histories']
      });
    }
  });
}

export function useRemoveCoupon() {
  return useMutation({
    mutationFn: (data: { business_id: number; code: string }) => removeCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['billing_history'],
        refetchType: 'active'
      });
      queryClient.refetchQueries({
        queryKey: ['billing_histories']
      });
    }
  });
}

export function usePayDues() {
  return useMutation({
    mutationFn: () => payDues(),
    onSuccess: () => {}
  });
}

export function useMarkBillingStatusPaid() {
  return useMutation({
    mutationFn: (id: number) => markBillingStatusPaid(id),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['billing_histories']
      });
    }
  });
}

export function useMarkBillingStatusVoid() {
  return useMutation({
    mutationFn: (id: number) => markBillingStatusVoid(id),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['billing_histories']
      });
    }
  });
}

export function useFetchBusinessInvoice() {
  return useMutation({
    mutationFn: (id: number) => fetchBusinessInvoice(id),
    onSuccess: (data: { url: string }) => {
      if (data.url) window.open(data.url, '_blank');
    }
  });
}

export function useGetBusinessInvoiceTransactions(
  invoiceId?: number,
  isBusinessView?: boolean,
  options?: UseQueryOptions<InvoiceTransactionResponse[], Error>
) {
  return useQuery<InvoiceTransactionResponse[]>({
    queryKey: ['business_invoice_transactions', invoiceId],
    queryFn: () => getBusinessInvoiceTransactions(invoiceId!),
    enabled: Boolean(invoiceId && isBusinessView),
    ...options
  });
}

export function useCancelBusinessSubscription() {
  return useMutation({
    mutationFn: (payload?: CancelSubscriptionPayload) => cancelBusinessSubscription(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing_history'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['billing_histories'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['billing-card-details'], refetchType: 'active' });
    }
  });
}
